import { Button } from "@/components/button";
import { UploadedImage } from "@/components/uploaded-image";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";
import { useQuery, useQueries } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useParams } from "react-router";
import styled from "@emotion/styled";
import { Uuid } from "@/domains/global/identifiers";

interface StaffEvent {
  id: string;
  staff_member_id: string;
  kind: string;
  event_date: string;
  payload: Record<string, unknown>;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  staff_member: {
    id: string;
    profile_display_name: string;
    profile_photo_uploaded_file_id: string | null;
  };
}

interface KudosEventPayload {
  from_staff_member_id: string;
  content: string;
}

interface KudosWithGiver {
  event: StaffEvent;
  giverName?: string;
  giverPhotoId?: string | null;
}

export const CompanyTeamMemberPage: FC = () => {
  const { staffMemberId } = useParams<{ staffMemberId: Uuid }>();
  const [postContent, setPostContent] = useState("");
  const [postImage, setPostImage] = useState<File | null>(null);
  const [isPosting, setIsPosting] = useState(false);

  const profile = useQuery({
    queryKey: ["staff-member", staffMemberId],
    enabled: !!staffMemberId,
    queryFn: () =>
      api.get("/internal/v1/staff/members/{staff_member_id}", {
        params: { path: { staff_member_id: staffMemberId! } },
      }),
  });

  const events = useQuery({
    queryKey: ["staff-events", staffMemberId],
    enabled: !!staffMemberId,
    queryFn: () =>
      api.post("/internal/v1/staff/events/search", {
        body: { staff_member_id: staffMemberId, limit: 20 },
      }),
  });

  const memberBadges = useQuery({
    queryKey: ["member-badges", staffMemberId],
    enabled: !!staffMemberId,
    queryFn: () =>
      api.post("/internal/v1/staff/member-badges/search", {
        body: { staff_member_id: staffMemberId, limit: 50 },
      }),
  });

  // Get current user's staff member info to check if viewing own profile
  const currentUser = useQuery({
    queryKey: ["current-staff-member"],
    queryFn: () => api.get("/internal/v1/me/staff/member"),
  });

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await api.delete("/internal/v1/staff/events/{staff_event_id}", {
        params: { path: { staff_event_id: eventId } },
      });
      events.refetch();
    } catch (error) {
      console.error("Failed to archive event:", error);
    }
  };

  const handleCreatePost = async () => {
    if (!staffMemberId || !postContent.trim()) return;

    setIsPosting(true);
    try {
      let photoUploadedFileId = null;
      if (postImage) {
        const { fileId } = await fileModule.uploadFile({ file: postImage });
        photoUploadedFileId = fileId;
      }

      await api.post("/internal/v1/staff/events", {
        body: {
          staff_member_id: staffMemberId,
          kind: "POST",
          event_date: new Date().toISOString(),
          payload: {
            content: postContent,
            photo_uploaded_file_id: photoUploadedFileId,
          },
        },
      });

      setPostContent("");
      setPostImage(null);
      events.refetch();
    } catch (error) {
      console.error("Failed to create post:", error);
    } finally {
      setIsPosting(false);
    }
  };

  const member = profile.data?.data;
  const eventsData: StaffEvent[] = events.data?.data?.results ?? [];
  const kudosEvents = eventsData.filter(event => event.kind === "KUDOS");
  const badgesData = memberBadges.data?.data?.results ?? [];
  const isOwnProfile = currentUser.data?.data?.id === staffMemberId;

  // Fetch staff member info for each kudos giver
  const kudosGiverQueries = useQueries({
    queries: kudosEvents.map(event => {
      const payload = event.payload as unknown as KudosEventPayload;
      return {
        queryKey: ["staff-member", payload.from_staff_member_id],
        queryFn: () =>
          api.get("/internal/v1/staff/members/{staff_member_id}", {
            params: { path: { staff_member_id: payload.from_staff_member_id } },
          }),
        enabled: !!payload.from_staff_member_id,
      };
    }),
  });

  // Combine kudos events with giver information
  const kudosWithGivers: KudosWithGiver[] = kudosEvents.map((event, index) => {
    const giverData = kudosGiverQueries[index]?.data?.data;
    return {
      event,
      giverName: giverData?.profile_display_name ?? "Unknown",
      giverPhotoId: giverData?.profile_photo_uploaded_file_id,
    };
  });

  return (
    <Container>
      {member ? (
        <ProfileLayout>
          <ProfileHeader>
            <ProfileImageWrapper>
              <UploadedImage uploadedFileId={member.profile_photo_uploaded_file_id} size={120} />
            </ProfileImageWrapper>
            <ProfileInfo>
              <ProfileName>{member.profile_display_name}</ProfileName>
              {member.profile_current_location && (
                <ProfileLocation>📍 {member.profile_current_location}</ProfileLocation>
              )}
              {member.profile_about_me && <ProfileAbout>{member.profile_about_me}</ProfileAbout>}
            </ProfileInfo>
          </ProfileHeader>

          {badgesData.length > 0 && (
            <Section>
              <SectionTitle>Badges</SectionTitle>
              <BadgesGrid>
                {badgesData.map(b => (
                  <BadgeItem key={b.id}>
                    <UploadedImage
                      uploadedFileId={b.staff_badge.image_uploaded_file_id}
                      size={60}
                    />
                    <BadgeName>{b.staff_badge.name}</BadgeName>
                    {b.context && <BadgeContext>{b.context}</BadgeContext>}
                  </BadgeItem>
                ))}
              </BadgesGrid>
            </Section>
          )}

          {isOwnProfile && (
            <Section>
              <SectionTitle>Create Post</SectionTitle>
              <PostForm>
                <PostTextArea
                  placeholder="What's on your mind?"
                  value={postContent}
                  onChange={e => setPostContent(e.target.value)}
                  rows={4}
                />
                <PostActions>
                  <FileInput
                    type="file"
                    accept="image/*"
                    onChange={e => setPostImage(e.target.files?.[0] ?? null)}
                  />
                  {postImage && <SelectedFile>Selected: {postImage.name}</SelectedFile>}
                  <Button onClick={handleCreatePost} disabled={!postContent.trim() || isPosting}>
                    {isPosting ? "Posting..." : "Post"}
                  </Button>
                </PostActions>
              </PostForm>
            </Section>
          )}

          {kudosWithGivers.length > 0 && (
            <Section>
              <SectionTitle>Recent Kudos</SectionTitle>
              <KudosList>
                {kudosWithGivers.map(({ event, giverName, giverPhotoId }) => {
                  const payload = event.payload as unknown as KudosEventPayload;
                  return (
                    <KudosItem key={event.id}>
                      <KudosHeader>
                        <KudosGiver>
                          {giverPhotoId && (
                            <KudosGiverPhoto>
                              <UploadedImage uploadedFileId={giverPhotoId} size={32} />
                            </KudosGiverPhoto>
                          )}
                          <KudosGiverName>From {giverName}</KudosGiverName>
                        </KudosGiver>
                        <KudosDate>{new Date(event.event_date).toLocaleDateString()}</KudosDate>
                      </KudosHeader>
                      <KudosContent>{payload.content}</KudosContent>
                    </KudosItem>
                  );
                })}
              </KudosList>
            </Section>
          )}

          {eventsData.filter(ev => ev.kind !== "KUDOS" && !ev.archived_at).length > 0 && (
            <Section>
              <SectionTitle>Recent Activity</SectionTitle>
              <ActivityList>
                {eventsData
                  .filter(ev => ev.kind !== "KUDOS" && !ev.archived_at)
                  .map(ev => {
                    const payload = ev.payload as {
                      content?: string;
                      photo_uploaded_file_id?: string;
                    };
                    return (
                      <ActivityItem key={ev.id}>
                        {payload.content && <ActivityContent>{payload.content}</ActivityContent>}
                        {payload.photo_uploaded_file_id && (
                          <ActivityImage>
                            <UploadedImage
                              uploadedFileId={payload.photo_uploaded_file_id}
                              size={340}
                            />
                          </ActivityImage>
                        )}
                        <ActivityFooter>
                          <ActivityDate>
                            {new Date(ev.event_date).toLocaleDateString()}
                          </ActivityDate>
                          {isOwnProfile && !ev.archived_at && (
                            <Button
                              variant="outline"
                              size="1"
                              onClick={() => handleDeleteEvent(ev.id)}
                            >
                              Delete
                            </Button>
                          )}
                        </ActivityFooter>
                      </ActivityItem>
                    );
                  })}
              </ActivityList>
            </Section>
          )}
        </ProfileLayout>
      ) : (
        <div>Loading...</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const ProfileLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ProfileHeader = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const ProfileImageWrapper = styled.div`
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h1`
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
`;

const ProfileLocation = styled.div`
  color: #666;
  font-size: 14px;
  margin-bottom: 12px;
`;

const ProfileAbout = styled.p`
  margin: 0;
  line-height: 1.6;
  color: #333;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const BadgesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
`;

const BadgeItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
`;

const BadgeName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

const BadgeContext = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const KudosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const KudosItem = styled.div`
  padding: 16px;
  background: #f0f8ff;
  border-radius: 8px;
  border-left: 4px solid #007bff;
`;

const KudosHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const KudosGiver = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const KudosGiverPhoto = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
`;

const KudosGiverName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

const KudosContent = styled.div`
  line-height: 1.5;
  color: #333;
`;

const KudosDate = styled.div`
  font-size: 12px;
  color: #666;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActivityItem = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const ActivityContent = styled.div`
  margin-bottom: 8px;
`;

const ActivityImage = styled.div`
  margin: 12px 0;
`;

const ActivityDate = styled.div`
  font-size: 12px;
  color: #666;
`;

const PostForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PostTextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const PostActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FileInput = styled.input`
  font-size: 14px;
  flex: 1;
`;

const SelectedFile = styled.span`
  font-size: 14px;
  color: #666;
`;

const ActivityFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;
